import { LitElement, html, Splide, animationHelpers } from '@hh/global.js'
import { getPlpStickyStackOffsetPx } from '@hh/component-anchor-target-utils.js'

/**
 * No-configuration sticky nav: links are built from whichever `[data-subcollection
 * -section]` elements are present (and currently visible) on the page, not from admin
 * -configured blocks. Scrollspy / slider mechanics mirror `ContentStickyNav`
 * (`section-content-sticky-nav.js`).
 *
 * Sticky `top` offset and the "go to top" progress ring both mirror
 * `PlpAnchorTargetAtelier` (`component-plp-anchor-target-atelier.js` /
 * `section-plp-anchor-target-atelier.css`) exactly: the CSS `top` is driven purely by
 * `--hh-plp-anchor-below-filters`, a var `component-collection-filters.js` keeps in sync
 * with the filter bar's own height - no per-scroll JS needed for positioning, which is
 * why that mechanism stays smooth. This *requires* this section to be placed directly
 * after `main-collection-filters` (and before the Subcollection sections) in the
 * template, same as the Atelier section's own doc comment states - the var only
 * represents "the filter bar's height", not "how far this section is from the top", so
 * if something else sits between them the offset will be wrong.
 */
export class AnchorTargetsNav extends LitElement {
  static properties = {
    sectionId: {
      typeof: 'String',
    },
  }

  constructor() {
    super()
    this.progressRaf = null
    this.onRootClick = this.onRootClick.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.list = this.querySelector('[data-anchor-targets-list]')
    this.addEventListener('click', this.onRootClick)

    // Wait a frame: this section can render before the `subcollection` sections below it
    // on the page, and their `component-subcollection-grid.js` (which stamps the real
    // id/title onto each `[data-subcollection-section]` - see `syncSectionIdentity`)
    // needs to run first. Module scripts execute in document order before first paint,
    // so by the next animation frame every section's own script has already run.
    requestAnimationFrame(() => {
      this.buildLinks()
      this.initSlider()
      this.scrollCheck()
    })

    this.boundOnWindowScroll = this.onWindowScroll.bind(this)
    this.boundRebuild = this.rebuild.bind(this)
    window.addEventListener('scroll', this.boundOnWindowScroll, { passive: true })
    window.addEventListener('resize', this.boundOnWindowScroll)
    window.addEventListener('plp:filter-update', this.boundRebuild)
    window.addEventListener('popstate', this.boundRebuild)

    this.updateProgressRing()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.onRootClick)
    window.removeEventListener('scroll', this.boundOnWindowScroll)
    window.removeEventListener('resize', this.boundOnWindowScroll)
    window.removeEventListener('plp:filter-update', this.boundRebuild)
    window.removeEventListener('popstate', this.boundRebuild)
    if (this.progressRaf != null) {
      cancelAnimationFrame(this.progressRaf)
      this.progressRaf = null
    }
  }

  rebuild() {
    // `component-subcollection-grid.js`'s `updateGiftGuideFilterState` also defers via
    // `queueMicrotask` on this same event; since this section's script registers first
    // (it sits above the Subcollection sections in the template), a same-priority
    // microtask here would run *before* that one and read stale `section.hidden` values.
    // `requestAnimationFrame` runs after the microtask queue fully drains, guaranteeing
    // this reads the post-update state regardless of listener registration order.
    requestAnimationFrame(() => {
      this.buildLinks()
      this.initSlider()
      this.scrollCheck()
    })
  }

  onRootClick(event) {
    const topButton = event.target?.closest?.('[data-anchor-targets-go-to-top]')
    if (topButton && this.contains(topButton)) {
      event.preventDefault()
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' })
    }
  }

  onWindowScroll() {
    this.scrollCheck()
    this.scheduleProgressRingUpdate()
  }

  getPageScrollProgress() {
    const scroller = document.scrollingElement
    if (!scroller) {
      return 0
    }
    const maxY = scroller.scrollHeight - scroller.clientHeight
    if (maxY <= 0) {
      return 0
    }
    return Math.min(1, Math.max(0, scroller.scrollTop / maxY))
  }

  getRingLengthPixels(ring) {
    if (ring instanceof SVGCircleElement) {
      try {
        // Throws "non-rendered element" if this bar (or an ancestor) is currently
        // `display: none` - e.g. no subcollection on the page has a title yet.
        const n = ring.getTotalLength()
        if (Number.isFinite(n) && n > 0) {
          return n
        }
      } catch {
        // fall through to the geometric estimate below
      }
    }
    const r = Number.parseFloat(String(ring.getAttribute('r') ?? '16')) || 16
    return 2 * Math.PI * r
  }

  updateProgressRing() {
    this.progressRaf = null
    const progress = this.getPageScrollProgress()
    for (const ring of this.querySelectorAll('[data-anchor-targets-progress-ring]')) {
      if (!(ring instanceof SVGCircleElement)) {
        continue
      }
      const len = this.getRingLengthPixels(ring)
      if (!Number.isFinite(len) || len <= 0) {
        continue
      }
      ring.style.strokeDasharray = String(len)
      ring.style.strokeDashoffset = String(len * (1 - progress))
    }
  }

  scheduleProgressRingUpdate() {
    if (this.progressRaf != null) {
      return
    }
    this.progressRaf = requestAnimationFrame(() => this.updateProgressRing())
  }

  scrollCheck() {
    const sections = this.sectionTargets ?? []
    let mostScrollThroughPercent = null
    let activeSlideLink = null
    let activeLinkIndex = null

    sections.forEach((section, index) => {
      const scrollPercent = animationHelpers.scrollThroughPercentage(section)
      const slideLink = this.slideLinks[index]
      slideLink?.classList.remove('active')

      if (
        index !== 0 &&
        (scrollPercent == null ||
          scrollPercent < 0 ||
          (mostScrollThroughPercent !== null &&
            mostScrollThroughPercent < scrollPercent))
      ) {
        return null
      }

      activeSlideLink = slideLink
      activeLinkIndex = index
      mostScrollThroughPercent = scrollPercent
    })

    activeSlideLink?.classList.add('active')
    if (activeLinkIndex !== null) {
      this.slider?.go(activeLinkIndex)
    }
  }

  buildLinks() {
    if (!this.list) {
      return
    }

    this.list.innerHTML = ''

    const sections = [
      ...document.querySelectorAll('[data-subcollection-section]'),
    ].filter((section) => !section.hidden && section.dataset.subcollectionTitle)

    this.sectionTargets = sections

    this.slideLinks = sections.map((section) => {
      const li = document.createElement('li')
      li.className = 'anchor-targets-nav__slide-item splide__slide'

      const anchor = document.createElement('a')
      anchor.href = `#${section.id}`
      anchor.className = 'anchor-targets-nav__link body'
      anchor.textContent = section.dataset.subcollectionTitle
      anchor.addEventListener('click', (event) => this.clickLink(event, section))

      li.appendChild(anchor)
      this.list.appendChild(li)
      return anchor
    })

    this.hidden = sections.length === 0
  }

  clickLink(event, linkSectionTarget) {
    if (!linkSectionTarget) {
      return null
    }
    event.preventDefault()

    const offset = getPlpStickyStackOffsetPx()
    const top = linkSectionTarget.getBoundingClientRect().top + window.scrollY - offset

    animationHelpers.scrollToSmoothly(Math.max(0, top), 500)
  }

  initSlider() {
    const sliderHolder = this.querySelector('[data-slider-holder]')
    if (!sliderHolder) {
      return null
    }

    this.slider?.destroy()

    this.slider = new Splide(sliderHolder, {
      type: 'slide',
      arrows: false,
      pagination: false,
      autoplay: false,
      pauseOnHover: false,
      perPage: 1,
      useScroll: true,
      drag: 'free',
      autoWidth: true,
      snap: false,
      trimSpace: true,
    })

    this.slider.mount()
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('anchor-targets-nav', AnchorTargetsNav)
